/**
 * WritebackContext — one source of truth for "what f8 learned" on web
 * (PPL-265). Port of the app's context with the same invariants:
 *
 * The count is the SAME NUMBER everywhere it appears — the rail badge, the
 * insights sub-row and the page header — which only holds if all of them read
 * one subscription, so this provider owns it.
 *
 * ── the sticky-resolution problem ────────────────────────────────────
 * The live query is `status == 'pending'`, so an accepted item leaves the
 * snapshot instantly. But the design requires the opposite of a
 * disappearance: a resolved item collapses IN PLACE to a one-line receipt
 * with undo. So resolved items are held in `resolved` until the page is left,
 * and the page renders pending ∪ resolved. Badges read only unsettled items,
 * which is what makes them count down live as the user works.
 *
 * ── dismiss, and why it is deferred ──────────────────────────────────
 * The design's "dismissed → undo" must be honest: the server's `reject` is
 * terminal (it also tombstones the alias for identity types) — there is no
 * un-reject. So the card collapses immediately but the callable only fires
 * when the undo window closes; undo inside the window cancels the send,
 * leaving the suggestion genuinely still pending. In-flight dismissals are
 * flushed on pagehide via a keepalive fetch (an awaited callable is aborted
 * at teardown), so closing the tab commits best-effort rather than silently
 * discarding.
 *
 * ── what f8 saved by itself (PPL-454) ────────────────────────────────
 * A strong match now saves itself server-side and the doc lands
 * `status:'accepted'` with `appliedAt`, so it is never in the pending
 * snapshot and no surface would otherwise show it. A second subscription
 * carries those, and they are turned into a READ-ONLY resolved overlay that
 * sits UNDER the local map: every existing consumer then renders them
 * through the receipt row it already has, and a local undo still wins over
 * the server's "saved" while the call is in flight. Badges keep reading the
 * LOCAL map — a receipt is not work to do.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import { FIREBASE_AUTH } from '../firebaseConfig';
import {
  describeSuggestion,
  respondToSuggestion,
  subscribeAutoSavedSuggestions,
  subscribePendingSuggestions,
  subscribePeopleNames,
  rejectSuggestionKeepalive,
  undoWritebackItem,
  type RespondResult,
  type SuggestionItem,
} from '../services/writebackService';

export type ResolvedState = {
  status: 'accepted' | 'dismissed' | 'undone' | 'conflicted';
  undoable: boolean;
  pendingCall?: boolean;
  personName?: string | null;
  /**
   * Whether the accept actually CREATED a contact — the server's own answer,
   * not an inference from the suggestion type. A `new_person` item can be
   * answered by picking an existing candidate (a demoted rejected-alias match
   * comes back as `new_person` with that person still listed), in which case
   * nothing is created and a receipt claiming otherwise is simply false.
   */
  createdPerson?: boolean;
  /**
   * The server applied this one on its own (PPL-454) — the flag that turns
   * on the score and the "not <name>?" affordance on the receipt. Never set
   * by a local accept: the user decided that one, and a score on it is
   * noise.
   */
  autoSaved?: boolean;
};

interface WritebackValue {
  ready: boolean;
  error: Error | null;
  pending: SuggestionItem[];
  /** Items f8 saved by itself in the last 7 days — receipts, not work. */
  autoSaved: SuggestionItem[];
  resolved: Record<string, ResolvedState>;
  pendingTotal: number;
  people: Record<string, { name: string; firstName: string | null }>;
  resolve: (
    id: string,
    action: 'accept' | 'edit' | 'reject',
    options?: { edits?: Record<string, string>; chosenPersonId?: string },
  ) => Promise<RespondResult | null>;
  undo: (id: string, reason?: 'wrong_person') => Promise<boolean>;
  dismiss: (id: string) => void;
  undoDismiss: (id: string) => boolean;
  clearResolved: (ids?: string[]) => void;
}

const EMPTY: WritebackValue = {
  ready: false,
  error: null,
  pending: [],
  autoSaved: [],
  resolved: {},
  pendingTotal: 0,
  people: {},
  resolve: async () => null,
  undo: async () => false,
  dismiss: () => {},
  undoDismiss: () => false,
  clearResolved: () => {},
};

const WritebackContext = createContext<WritebackValue>(EMPTY);

// Does not throw when the provider is absent: surfaces that show the badge
// must degrade to "nothing pending", never to a crash.
export const useWriteback = () => useContext(WritebackContext);

const DISMISS_UNDO_MS = 5000;

export function WritebackProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const uid: string | null = user?.uid || null;

  const [rawSuggestions, setRawSuggestions] = useState<Record<string, any>[]>([]);
  const [rawAutoSaved, setRawAutoSaved] = useState<Record<string, any>[]>([]);
  const [people, setPeople] = useState<Record<string, { name: string; firstName: string | null }>>({});
  const [resolved, setResolved] = useState<Record<string, ResolvedState>>({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Last-known description of every resolved item, so its receipt row can
  // still render after it has left the pending snapshot.
  const snapshotRef = useRef<Record<string, SuggestionItem>>({});

  // ── pending suggestions ────────────────────────────────────────────
  useEffect(() => {
    if (!uid) {
      setRawSuggestions([]);
      setReady(false);
      return undefined;
    }
    setError(null);
    const unsub = subscribePendingSuggestions(
      uid,
      (rows) => { setRawSuggestions(rows); setReady(true); },
      (err) => { setError(err); setReady(true); },
    );
    return unsub;
  }, [uid]);

  // ── what f8 saved by itself ────────────────────────────────────────
  // Mounted on the same uid as the pending query. A failure here is NOT
  // surfaced as the page's `error`: the review queue still works without
  // receipts, and blanking the queue because a receipt list failed to load
  // would be the worse outcome.
  useEffect(() => {
    if (!uid) {
      setRawAutoSaved([]);
      return undefined;
    }
    return subscribeAutoSavedSuggestions(uid, setRawAutoSaved);
  }, [uid]);

  // ── people names — gated on there being something to name ──────────
  // This listener duplicates the contacts page's own; mounting it
  // unconditionally would add a full people-collection read to every visit
  // with nothing pending, and Firestore read growth is a tracked cost lever.
  // Receipts name a person too — leaving them out here left every auto-saved
  // row reading "saved to their timeline" forever.
  const needsNames = rawSuggestions.length + rawAutoSaved.length > 0;
  useEffect(() => {
    if (!uid || !needsNames) {
      setPeople({});
      return undefined;
    }
    return subscribePeopleNames(uid, setPeople);
  }, [uid, needsNames]);

  // ── derived ────────────────────────────────────────────────────────
  const pending = useMemo(
    () => rawSuggestions.map((raw) => describeSuggestion(raw, people[raw.personId]?.name || null)),
    [rawSuggestions, people],
  );

  const autoSaved = useMemo(
    () => rawAutoSaved.map((raw) => describeSuggestion(raw, people[raw.personId]?.name || null)),
    [rawAutoSaved, people],
  );

  useEffect(() => {
    const store = snapshotRef.current;
    pending.forEach((item) => { store[item.id] = item; });
    autoSaved.forEach((item) => { store[item.id] = item; });
  }, [pending, autoSaved]);

  /**
   * The server's own "this is settled" overlay. Read-only: it is derived
   * from the subscription, never written to, and it is merged UNDER the
   * local map so an undo in progress (or a just-landed accept) still wins.
   */
  const serverResolved = useMemo<Record<string, ResolvedState>>(
    () => Object.fromEntries(autoSaved.map((i) => [i.id, {
      status: 'accepted' as const,
      undoable: true,
      autoSaved: true,
      personName: i.personName,
      createdPerson: false,
    }])),
    [autoSaved],
  );

  const resolvedView = useMemo(
    () => ({ ...serverResolved, ...resolved }),
    [serverResolved, resolved],
  );

  // Badges exclude anything settled locally: an accepted item already left
  // the snapshot, but a DISMISSED one has not (its reject is inside the undo
  // window), and leaving it counted would freeze the badge for those seconds.
  const pendingTotal = useMemo(
    () => pending.filter((i) => !resolved[i.id]).length,
    [pending, resolved],
  );

  // ── mutations ──────────────────────────────────────────────────────
  const resolve = useCallback<WritebackValue['resolve']>(async (id, action, options = {}) => {
    setResolved((r) => ({ ...r, [id]: { status: 'accepted', undoable: false, pendingCall: true } }));
    try {
      const result = await respondToSuggestion({
        suggestionId: id,
        action,
        edits: options.edits,
        chosenPersonId: options.chosenPersonId,
      });
      // `already_resolved` is NOT an accept: it means a stale snapshot or the
      // same item settled elsewhere (the app, another tab). `previousStatus`
      // decides what the receipt says — claiming "saved" over a rejected or
      // expired item would offer an undo that comes back `gone`.
      const settled =
        result?.status === 'already_resolved'
          ? String(result.previousStatus || '')
          : String(result?.status || 'accepted');
      const uiStatus: ResolvedState['status'] =
        settled === 'rejected' || settled === 'expired' ? 'dismissed'
          : settled === 'undone' ? 'undone'
            : settled === 'conflicted' ? 'conflicted'
              : 'accepted';
      setResolved((r) => ({
        ...r,
        [id]: {
          status: uiStatus,
          undoable: uiStatus === 'accepted',
          pendingCall: false,
          personName: result?.personName || null,
          createdPerson: result?.createdPerson === true,
        },
      }));
      return result;
    } catch (err) {
      console.warn('[writeback] resolve failed:', (err as Error)?.message);
      // Put it back — a failed accept must not read as saved.
      setResolved((r) => {
        const next = { ...r };
        delete next[id];
        return next;
      });
      return null;
    }
  }, []);

  /**
   * Undo an ACCEPTED item. Terminal on purpose: the server tombstones the
   * alias, so the receipt settles on "not saved" and stays counted out.
   * Returns false when the undo could not run (caller may toast).
   */
  const undo = useCallback(async (id: string, reason?: 'wrong_person') => {
    // An auto-saved receipt has no LOCAL entry — the overlay is where its
    // "accepted" came from, so the optimistic state has to start there or
    // the row would lose the person's name mid-undo.
    const previous = resolved[id] || serverResolved[id];
    setResolved((r) => ({ ...r, [id]: { ...(previous as ResolvedState), status: 'undone', undoable: false, pendingCall: true } }));
    try {
      const result = await undoWritebackItem(id, reason);
      // A refused rollback arrives as conflictedPaths — there is no
      // "conflicted" status on UndoWritebackResult. The user's own edited
      // value stands, and saying "not saved" would be a lie.
      const conflicted = (result?.conflictedPaths?.length || 0) > 0;
      setResolved((r) => ({
        ...r,
        [id]: {
          ...(previous as ResolvedState),
          status: conflicted ? 'conflicted' : 'undone',
          undoable: false,
          pendingCall: false,
        },
      }));
      return true;
    } catch (err) {
      console.warn('[writeback] undo failed:', (err as Error)?.message);
      setResolved((r) => ({ ...r, [id]: { ...(previous as ResolvedState), pendingCall: false } }));
      return false;
    }
  }, [resolved, serverResolved]);

  // ── deferred dismiss ───────────────────────────────────────────────
  const dismissTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const commitDismiss = useCallback(async (id: string) => {
    delete dismissTimers.current[id];
    try {
      await respondToSuggestion({ suggestionId: id, action: 'reject' });
      setResolved((r) => (r[id] ? { ...r, [id]: { status: 'dismissed', undoable: false } } : r));
    } catch (err) {
      console.warn('[writeback] dismiss failed:', (err as Error)?.message);
      setResolved((r) => {
        const next = { ...r };
        delete next[id];
        return next;
      });
    }
  }, []);

  // ID token cached ahead of time, because the pagehide flush has no room
  // for an async getIdToken() — the page is being torn down. Primed as soon
  // as the user is known (not just at dismiss time: a user who hits `x` and
  // closes the tab in the same instant would otherwise beat even the
  // in-memory cache read) and refreshed on every dismiss to stay current.
  const idTokenRef = useRef<string | null>(null);

  useEffect(() => {
    idTokenRef.current = null;
    if (!uid) return;
    FIREBASE_AUTH.currentUser?.getIdToken()
      .then((t) => { idTokenRef.current = t; })
      .catch(() => {});
  }, [uid]);

  const dismiss = useCallback((id: string) => {
    setResolved((r) => ({ ...r, [id]: { status: 'dismissed', undoable: true } }));
    dismissTimers.current[id] = setTimeout(() => commitDismiss(id), DISMISS_UNDO_MS);
    FIREBASE_AUTH.currentUser?.getIdToken()
      .then((t) => { idTokenRef.current = t; })
      .catch(() => {});
  }, [commitDismiss]);

  const undoDismiss = useCallback((id: string) => {
    const timer = dismissTimers.current[id];
    if (!timer) return false; // window closed — the reject has landed
    clearTimeout(timer);
    delete dismissTimers.current[id];
    setResolved((r) => {
      const next = { ...r };
      delete next[id];
      return next;
    });
    return true;
  }, []);

  // Closing the tab inside the undo window must COMMIT the dismissal, not
  // silently drop it. Two flush paths because the browser treats them
  // differently:
  //   - unmount (SPA navigation, sign-out): the page is alive, the normal
  //     callable works.
  //   - pagehide (tab close, hard navigation): an awaited httpsCallable is
  //     routinely ABORTED at teardown, so the reject would silently never
  //     land and the item would be back next visit. `keepalive` fetch is the
  //     mechanism built for this moment; best-effort, but the browser
  //     actually delivers it. (The app's AppState→background flush has no
  //     such problem — the process outlives the handler there.)
  useEffect(() => {
    const flushViaKeepalive = () => {
      const token = idTokenRef.current;
      Object.keys(dismissTimers.current).forEach((id) => {
        clearTimeout(dismissTimers.current[id]);
        delete dismissTimers.current[id];
        if (token) rejectSuggestionKeepalive(id, token);
      });
    };
    const flushViaCallable = () => {
      Object.keys(dismissTimers.current).forEach((id) => {
        clearTimeout(dismissTimers.current[id]);
        commitDismiss(id);
      });
    };
    window.addEventListener('pagehide', flushViaKeepalive);
    return () => {
      window.removeEventListener('pagehide', flushViaKeepalive);
      flushViaCallable();
    };
  }, [commitDismiss]);

  /**
   * Drop the local overlay for these ids (or everything, on sign-out) so the
   * next visit starts from the server's truth. Scoped by id because more than
   * one review surface can exist at once.
   *
   * Only the LOCAL map: `serverResolved` is derived from a live query and
   * clearing it would just mean re-deriving it on the next snapshot, while
   * hiding receipts that are still inside their undo window.
   */
  const clearResolved = useCallback((ids?: string[]) => {
    if (!ids) { setResolved({}); return; }
    setResolved((r) => {
      const next = { ...r };
      ids.forEach((id) => { delete next[id]; });
      return next;
    });
  }, []);

  const value = useMemo<WritebackValue>(
    () => ({
      ready, error, pending, autoSaved, resolved: resolvedView, pendingTotal, people,
      resolve, undo, dismiss, undoDismiss, clearResolved,
    }),
    [ready, error, pending, autoSaved, resolvedView, pendingTotal, people, resolve, undo, dismiss, undoDismiss, clearResolved],
  );

  return <WritebackContext.Provider value={value}>{children}</WritebackContext.Provider>;
}

/**
 * Items for one person (or all), pending ∪ auto-saved ∪ locally-resolved,
 * newest first. Auto-saved items are merged here rather than at each call
 * site so every review surface — the page, the per-person sidebar — shows
 * the receipt without a branching change of its own.
 */
export function useWritebackItems(personId?: string | null): SuggestionItem[] {
  const { pending, autoSaved, resolved } = useWriteback();
  const store = useRef<Record<string, SuggestionItem>>({});
  pending.forEach((i) => { store.current[i.id] = i; });
  autoSaved.forEach((i) => { store.current[i.id] = i; });
  return useMemo(() => {
    const base = [
      ...pending,
      ...autoSaved.filter((i) => !pending.some((p) => p.id === i.id)),
    ];
    const resolvedItems = Object.keys(resolved)
      .map((id) => store.current[id])
      .filter(Boolean) as SuggestionItem[];
    const all = [
      ...base,
      ...resolvedItems.filter((i) => !base.some((p) => p.id === i.id)),
    ];
    const scoped = personId === undefined ? all : all.filter((i) => i.personId === personId);
    return scoped.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
  }, [pending, autoSaved, resolved, personId]);
}

export default WritebackContext;
